/**
 * 스케줄 하루 미리보기 — 규칙이 하루를 어떻게 나누는지 그림으로 보여준다.
 *
 * **저장 전에 보여야 의미가 있다.** 심야 구간(22:00~02:00)이나 겹치는 규칙은 머릿속으로
 * 검증하기 어렵고, 틀린 편성은 저장한 뒤 그 시간이 와야 드러난다 — 대개 아무도 안 보는
 * 새벽에. 그래서 서버의 `GET /schedules/:id/preview` 를 부르지 않고 **편집 중인 규칙을
 * 그 자리에서** 해석한다. 서버에 없는(아직 저장 안 한) 상태를 보여주는 것이 요점이다.
 *
 * 해석은 `@signageone/core` 의 `resolveSchedule` 을 그대로 쓴다. 서버·플레이어와 같은
 * 함수라, 여기 보이는 그림과 실제 재생이 어긋날 수 없다 — core 가 존재하는 이유다.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  resolveSchedule,
  zonedDayStart,
  zonedFieldsAt,
  zonedNextDayStart,
  zonedYmd,
  type ScheduleRef,
} from '@signageone/core';
import type { Asset, Playlist, ScheduleRule } from '../api/client.js';
import { nearestWeekday, ruleWeekdays, weekdayList } from './scheduleRules.js';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

/** 구간 색. 플레이리스트마다 다른 색을 주되 상태색(초록·노랑·빨강)은 피한다 — 그 자리는 경보가 쓴다. */
const SEGMENT_TONES = ['a', 'b', 'c', 'd', 'e', 'f'] as const;

export interface PreviewSegment {
  fromMinutes: number;
  toMinutes: number;
  playlistId: string | null;
  /** 규칙이 콘텐츠 하나를 가리켰을 때. 그때 `playlistId` 는 null 이다. */
  assetId: string | null;
  ruleId: string | null;
}

/**
 * 구간을 구분하는 열쇠 — 색과 접기(fold)의 기준.
 *
 * 플레이리스트 id 만 쓰면 **콘텐츠 규칙이 전부 같은 칸으로 보인다**(둘 다 null 이라
 * 이어 붙는다). 종류를 섞은 키를 쓰면 서로 다른 콘텐츠가 다른 구간·다른 색이 된다.
 */
export function segmentKey(segment: Pick<PreviewSegment, 'playlistId' | 'assetId'>): string | null {
  if (segment.playlistId) return `pl:${segment.playlistId}`;
  if (segment.assetId) return `as:${segment.assetId}`;
  return null;
}

/**
 * 하루를 훑어 구간으로 접는다.
 *
 * 창은 **그 타임존의 자정부터 다음 자정까지**다. UTC 자정에서 훑으면 보여주는 하루가
 * 오프셋만큼 밀리고, 서울이면 아홉 시간이라 새벽 규칙이 통째로 빠진다.
 * 서머타임이 있는 지역의 하루는 23시간이거나 25시간이라 끝도 다음 자정으로 잡는다.
 */
export function buildSegments(
  rules: readonly ScheduleRule[],
  /** 평소 편성 — 플레이리스트 id(옛 호출부), 또는 플레이리스트·콘텐츠 둘 중 하나(2026-10-21). */
  defaults: string | null | { playlistId: string | null; assetId?: string | null },
  timezone: string,
  ymd: string,
  /**
   * **1분이어야 한다.** 규칙의 시간 해상도가 `HH:MM` 이라 그보다 성기게 훑으면 규칙이
   * 격자에 맞춰 어긋난다 — 10분 격자에서는 `09:05~18:35` 이 `09:10~18:40` 으로 보이고,
   * `16:12~16:15` 처럼 격자 사이에 통째로 들어가는 짧은 규칙은 **아예 사라진다.**
   * 미리보기는 저장 전에 눈으로 확인하라고 만든 것이라, 여기서 어긋나면 존재 이유가 없다.
   *
   * 하루 1,440번을 훑지만 규칙 목록을 걸러 보는 것이 전부이고, 규칙이 바뀔 때만 다시 돈다.
   */
  stepMinutes = 1,
): { segments: PreviewSegment[]; totalMinutes: number } {
  const start = zonedDayStart(ymd, timezone);
  const end = zonedNextDayStart(ymd, timezone);
  if (!start || !end) return { segments: [], totalMinutes: 0 };

  const ref: ScheduleRef = {
    id: 'preview',
    timezone,
    defaultPlaylistId: typeof defaults === 'object' && defaults !== null ? defaults.playlistId : defaults,
    defaultAssetId: typeof defaults === 'object' && defaults !== null ? (defaults.assetId ?? null) : null,
    rules: rules
      // 대상을 아직 고르지 않은 규칙은 해석에서 뺀다 — 없는 편성을 그리지 않는다.
      .filter((rule) => rule.playlist_id || rule.asset_id)
      .map((rule) => ({
        id: rule.id,
        label: rule.label,
        days: rule.days,
        start: rule.start,
        end: rule.end,
        playlistId: rule.playlist_id,
        assetId: rule.asset_id,
        priority: rule.priority,
      })),
    revision: 0,
  };

  const totalMinutes = Math.round((end.getTime() - start.getTime()) / 60_000);
  const segments: PreviewSegment[] = [];

  for (let minute = 0; minute < totalMinutes; minute += stepMinutes) {
    const at = new Date(start.getTime() + minute * 60_000);
    const match = resolveSchedule(ref, at, timezone);
    const until = Math.min(minute + stepMinutes, totalMinutes);
    const previous = segments.at(-1);
    if (
      previous &&
      previous.playlistId === match.playlistId &&
      previous.assetId === match.assetId &&
      previous.ruleId === match.ruleId
    ) {
      previous.toMinutes = until;
    } else {
      segments.push({
        fromMinutes: minute,
        toMinutes: until,
        playlistId: match.playlistId,
        assetId: match.assetId,
        ruleId: match.ruleId,
      });
    }
  }

  return { segments, totalMinutes };
}

function hhmm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * 그 요일에 해당하는 **다음(오늘 포함)** 날짜. 기준은 스케줄 타임존의 오늘.
 *
 * 요일만 고르게 하고 날짜는 이쪽이 정한다 — 운영자가 검증하고 싶은 것은 "화요일에 뭐가
 * 나가나" 이지 특정 날짜가 아니다. 날짜 입력을 주면 오히려 요일을 세어 봐야 한다.
 */
function dateForWeekday(timezone: string, weekday: number, now: Date): string {
  const todayYmd = zonedYmd(now, timezone);
  const todayStart = zonedDayStart(todayYmd, timezone);
  if (!todayStart) return todayYmd;

  const offsetDays = (weekday - zonedFieldsAt(todayStart, timezone).day + 7) % 7;
  // 정오를 더해서 옮긴다 — 자정에 맞춰 더하면 서머타임 전환일에 하루가 어긋난다.
  const target = new Date(todayStart.getTime() + (offsetDays * 24 + 12) * 3600_000);
  return zonedYmd(target, timezone);
}

interface Props {
  rules: readonly ScheduleRule[];
  defaultPlaylistId: string | null;
  /** 평소 편성이 콘텐츠 하나일 때. */
  defaultAssetId?: string | null;
  timezone: string;
  playlists: readonly Playlist[];
  /** 규칙이 콘텐츠를 직접 가리킬 수 있어 이름표에 필요하다. 없으면 id 로 적는다. */
  assets?: readonly Asset[];
  /** 시각 주입 — 테스트가 "오늘"에 흔들리지 않게 한다. */
  now?: Date;
  /**
   * 지금 시각을 막대에 표시할지.
   *
   * 편집기에서는 끈다 — 거기서 보는 것은 "이 규칙이 하루를 어떻게 나누나" 이고,
   * 지금이 어디인지는 판단에 끼어들 뿐이다. 화면 상세에서는 켠다 — 거기서 묻는 것은
   * **"이 화면이 지금 무엇을 트고 다음엔 언제 바뀌나"** 이기 때문이다.
   */
  showNow?: boolean;
  /**
   * 막대나 구간을 눌렀을 때. 주면 시각을 고를 수 있는 미리보기가 된다.
   *
   * 편집기에서는 주지 않는다 — 거기서 하루는 규칙을 검증하는 그림이지 고르는 대상이
   * 아니다. 화면 상세에서만 "이 시각에 무엇이 나오나" 를 묻는다.
   */
  onPick?: (ymd: string, minutes: number) => void;
  /**
   * 끄는 동안 계속 불린다. `onPick` 은 놓을 때 한 번만 불린다.
   *
   * **둘을 나눈 이유는 서버다.** 끄는 내내 편성을 조회하면 손가락 한 번에 수십 번을
   * 두드린다. 끄는 동안에는 화면의 시각 표시만 따라가고, 실제 조회는 놓을 때 한다.
   */
  onScrub?: (ymd: string, minutes: number) => void;
  /** 고른 자리에 표시를 남긴다. 하루의 어디를 보고 있는지가 막대에서 보인다. */
  markMinutes?: number | null;
  /**
   * 구간 목록을 접어 둔다.
   *
   * 편집기에서는 펼쳐 둔다 — 거기서 목록은 규칙이 먹었는지 확인하는 답안지다.
   * 화면 상세에서는 접는다: 막대가 이미 하루를 다 말하고 있고, 구간이 열댓 개인 화면에서는
   * 이 목록이 그 아래 카드들을 통째로 화면 밖으로 밀어낸다.
   *
   * **키보드 경로는 그대로다** — 막대는 누를 수 없고 이 목록의 버튼이 시각을 고르는
   * 유일한 키보드 길이라, 접더라도 손잡이(버튼)로 펼쳐 닿을 수 있어야 한다.
   */
  foldList?: boolean;
  /** 요일 칩 줄 오른쪽에 붙일 것. 시각 입력이 온다. */
  headerRight?: ReactNode;
  /**
   * 이 규칙이 만든 구간을 도드라지게 한다 — 나머지는 흐려진다.
   *
   * **편집기에서 "이 규칙이 하루의 어디인가" 에 답하는 유일한 길이다.** 규칙은 요일·시각·
   * 우선순위로 적히는데, 그것이 하루의 어느 칸이 되는지는 머릿속으로 겹침까지 계산해야
   * 알 수 있다. 규칙에 손을 올리면 그 답이 막대에 그대로 뜬다.
   */
  highlightRuleId?: string | null;
  /**
   * 구간을 눌렀을 때 그 구간을 만든 규칙을 알려 준다. 편집기가 그 규칙으로 데려간다.
   *
   * `onPick`(시각 고르기)과 함께 주지 않는다 — 한 번 누르는 데 두 가지 뜻을 담으면
   * 누르는 사람이 무엇이 일어날지 예측할 수 없다.
   */
  onRulePick?: (ruleId: string) => void;
  /**
   * 고른 규칙 — 지금 보는 요일에 그 규칙이 없으면 그 규칙이 나오는 가장 가까운 요일로 넘어간다.
   *
   * `highlightRuleId` 와 따로 받는 이유: 그쪽은 마우스를 올리기만 해도 바뀐다. 목록을 훑으며
   * 지나가는 손에 그림의 요일까지 따라 바뀌면 화면이 계속 들썩인다. 넘어가는 것은 규칙을
   * **고를 때**(누르거나 칸에 들어갈 때)뿐이다.
   */
  followRuleId?: string | null;
}

export function SchedulePreview({
  rules,
  defaultPlaylistId,
  defaultAssetId = null,
  timezone,
  playlists,
  assets = [],
  now,
  showNow = false,
  onPick,
  onScrub,
  markMinutes = null,
  headerRight,
  foldList = false,
  highlightRuleId = null,
  onRulePick,
  followRuleId = null,
}: Props): ReactNode {
  const at = now ?? new Date();
  const todayWeekday = useMemo(() => zonedFieldsAt(at, timezone).day, [at, timezone]);

  const [weekday, setWeekday] = useState<number>(todayWeekday);
  const [scrubbing, setScrubbing] = useState(false);
  /* 접어 두는 쪽만 닫힌 채로 시작한다. 한 번 펼치면 요일을 바꿔도 펼친 채로 둔다. */
  const [listOpen, setListOpen] = useState(!foldList);
  const ymd = useMemo(() => dateForWeekday(timezone, weekday, at), [timezone, weekday, at]);
  const { segments, totalMinutes } = useMemo(
    () => buildSegments(rules, { playlistId: defaultPlaylistId, assetId: defaultAssetId }, timezone, ymd),
    [rules, defaultPlaylistId, defaultAssetId, timezone, ymd],
  );

  /*
   * 규칙마다 나오는 요일. 요일 칩의 점과 강조가 이것을 쓴다.
   *
   * 규칙 이름을 한 글자 칠 때마다 한 주를 다시 훑지 않게, 판정에 쓰이는 값만으로 다시 센다.
   */
  const shape = JSON.stringify(rules.map((r) => [r.id, r.days, r.start, r.end, r.playlist_id, r.asset_id, r.priority]));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- shape 가 rules 의 판정에 쓰이는 값을 모두 담는다.
  const week = useMemo(() => ruleWeekdays(rules), [shape]);
  const focus = highlightRuleId ? (week.byRule.get(highlightRuleId) ?? null) : null;
  const followDays = followRuleId ? (week.byRule.get(followRuleId)?.playing ?? []) : [];
  const followKey = followDays.join(',');

  /*
   * 고른 규칙이 지금 보는 요일에 없으면 넘어간다. 규칙의 요일을 고쳐서 지금 요일이 빠져도 같다
   * (월요일을 보면서 월요일을 끄면 남은 요일로). 나오는 요일이 하나도 없으면 그대로 둔다 —
   * 옮길 곳이 없고, "어느 요일에도 나오지 않는다" 는 아래 한 줄이 말한다.
   */
  useEffect(() => {
    if (!followRuleId || followDays.length === 0) return;
    setWeekday((current) => (followDays.includes(current) ? current : nearestWeekday(current, followDays)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- followKey 가 followDays 를 담는다.
  }, [followRuleId, followKey]);

  const nameOf = (segment: Pick<PreviewSegment, 'playlistId' | 'assetId'>): string => {
    if (segment.assetId) {
      const asset = assets.find((a) => a.id === segment.assetId);
      return asset ? `콘텐츠 · ${asset.title}` : '(삭제된 콘텐츠)';
    }
    if (!segment.playlistId) return '대기 화면';
    return playlists.find((p) => p.id === segment.playlistId)?.name ?? '(삭제된 플레이리스트)';
  };

  // 대상마다 색을 고정한다. 같은 편성이 하루에 여러 번 나와도 같은 색이어야 읽힌다.
  const tones = new Map<string, string>();
  for (const segment of segments) {
    const key = segmentKey(segment);
    if (key && !tones.has(key)) {
      tones.set(key, SEGMENT_TONES[tones.size % SEGMENT_TONES.length] as string);
    }
  }

  if (totalMinutes === 0) {
    return <p className="hint">타임존이 올바르지 않아 하루 편성을 계산할 수 없습니다.</p>;
  }

  /** 포인터가 가리키는 자리를 그 날의 분으로. */
  const minutesAt = (event: { currentTarget: HTMLElement; clientX: number }): number => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(0.999, Math.max(0, (event.clientX - rect.left) / rect.width));
    return Math.floor(ratio * totalMinutes);
  };

  /*
   * 지금이 이 하루의 어디인가. 보고 있는 날이 오늘일 때만 뜻이 있다 —
   * 화요일을 보면서 "지금" 을 그리면 그 선은 아무것도 가리키지 않는다.
   */
  const isToday = zonedYmd(at, timezone) === ymd;
  const nowMinutes = zonedFieldsAt(at, timezone).minutes;

  /*
   * 이름이 없는 규칙은 시간대로 부른다. "규칙" 이라고만 쓰면 구간이 여럿일 때
   * 어느 규칙이 만든 구간인지 알 수 없어, 목록이 있으나 마나가 된다.
   */
  const ruleLabel = (ruleId: string | null): string => {
    if (!ruleId) return '기본';
    const rule = rules.find((r) => r.id === ruleId);
    if (!rule) return '규칙';
    return rule.label.trim() || `${rule.start}~${rule.end}`;
  };

  return (
    <div className="sp">
      <div className="sp-head">
        {/*
          요일 칩 — 고르는 손잡이이면서 한 주의 요약이다.

          점은 그날 규칙이 하나라도 나온다는 뜻이다(점 없는 요일은 하루 종일 평소 편성). 규칙에
          손을 올리면 그 규칙이 나오는 요일에 테두리가 생기고 나머지는 흐려진다. 적힌 요일인데
          다른 규칙에 통째로 가려지는 날은 점선이다 — 적힌 요일과 나오는 요일은 다르다.
        */}
        <div className="daypicker" role="group" aria-label="미리볼 요일">
          {DAY_LABELS.map((label, day) => {
            const hasRules = week.playing.includes(day);
            const mark = focus
              ? focus.playing.includes(day)
                ? 'is-rule'
                : focus.matched.includes(day)
                  ? 'is-rule-hidden'
                  : 'is-rule-off'
              : '';
            const note = focus
              ? mark === 'is-rule'
                ? '이 규칙이 적용되는 요일'
                : mark === 'is-rule-hidden'
                  ? '다른 규칙에 가려지는 요일'
                  : '이 규칙이 없는 요일'
              : hasRules
                ? '시간대 규칙이 있는 요일'
                : '종일 기본 편성';
            return (
              <button
                key={day}
                type="button"
                className={`day sp-day ${weekday === day ? 'day-on' : ''} ${mark}`}
                aria-pressed={weekday === day}
                onClick={() => setWeekday(day)}
                title={`${label}요일 · ${note}`}
              >
                {label}
                {hasRules ? <span className="sp-day-dot" aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
        <span className="cell-muted mono sp-date">
          {ymd}
          {totalMinutes !== 1440 ? ` · ${Math.round((totalMinutes / 60) * 10) / 10}시간 (서머타임)` : ''}
        </span>
        {/* 시각을 고치는 손잡이는 고치는 대상 바로 위에 둔다. */}
        {headerRight ? <span className="sp-head-right">{headerRight}</span> : null}
      </div>

      {/*
        칩이 무엇을 말하는지 한 줄로. 규칙에 손을 올리면 그 규칙의 요일로 바뀐다 — 줄이 늘
        자리를 지키므로 손을 올렸다 뗄 때 아래 막대가 들썩이지 않는다.
      */}
      {week.playing.length > 0 || focus ? (
        <p className="sp-legend">
          {focus && highlightRuleId ? (
            <>
              <strong>{ruleLabel(highlightRuleId)}</strong>
              {focus.playing.length > 0
                ? ` · ${weekdayList(focus.playing)} 적용`
                : focus.matched.length === 0
                  ? ' · 지정된 요일 없음'
                  : ' · 다른 규칙에 가려져 적용되는 요일 없음'}
              {focus.playing.length > 0 && !focus.playing.includes(weekday)
                ? ` · ${DAY_LABELS[weekday]}요일 미적용`
                : ''}
            </>
          ) : (
            <>
              <span className="sp-day-dot sp-legend-dot" aria-hidden="true" />
              규칙이 있는 요일 · 점이 없으면 종일 기본 편성
            </>
          )}
        </p>
      ) : null}

      {/*
        누를 수 있을 때는 `img` 가 아니다 — 스크린리더에 그림이라고 말해 놓고 클릭을
        받으면 키보드 사용자에게는 없는 기능이 된다. 구간 목록의 버튼이 같은 일을 하므로
        막대 자체는 보조 수단으로 두고, 목록 쪽을 키보드 경로로 삼는다.
      */}
      <div
        className={`sp-bar ${onPick ? 'is-pickable' : ''}`}
        role="img"
        aria-label={`${ymd} 하루 편성 미리보기`}
        onPointerDown={
          onPick
            ? (event) => {
                /*
                 * **포인터를 붙잡는다.** 붙잡지 않으면 막대 밖으로 끌고 나가는 순간
                 * 이벤트가 끊겨 시각이 그 자리에 멈춘다 — 끝까지 끌 수가 없다.
                 */
                event.currentTarget.setPointerCapture(event.pointerId);
                setScrubbing(true);
                onScrub?.(ymd, minutesAt(event));
              }
            : undefined
        }
        onPointerMove={
          onPick && scrubbing ? (event) => onScrub?.(ymd, minutesAt(event)) : undefined
        }
        onPointerUp={
          onPick
            ? (event) => {
                if (!scrubbing) return;
                setScrubbing(false);
                // 놓을 때 한 번만 조회한다.
                onPick(ymd, minutesAt(event));
              }
            : undefined
        }
        onPointerCancel={onPick ? () => setScrubbing(false) : undefined}
      >
        {segments.map((segment) => {
          const left = (segment.fromMinutes / totalMinutes) * 100;
          const width = ((segment.toMinutes - segment.fromMinutes) / totalMinutes) * 100;
          const tone = tones.get(segmentKey(segment) ?? '') ?? null;
          return (
            <div
              key={segment.fromMinutes}
              className={`sp-seg ${tone ? `sp-t${tone}` : 'sp-idle'} ${
                highlightRuleId === null
                  ? ''
                  : segment.ruleId === highlightRuleId
                    ? 'is-hit'
                    : 'is-dim'
              }`}
              /*
               * 구간 사이에 실선을 둔다. 이웃한 구간이 같은 플레이리스트면 색이 같아서
               * 경계 없이 한 덩어리로 보이고, 그러면 규칙이 먹었는지 눈으로 알 수 없다.
               */
              style={{ left: `${left}%`, width: `${width}%` }}
              title={`${hhmm(segment.fromMinutes)}~${hhmm(segment.toMinutes)} · ${nameOf(segment)} (${ruleLabel(segment.ruleId)})`}
              /*
               * 편집기에서는 막대의 구간을 눌러도 그 규칙으로 간다 — 마우스로는 막대가 먼저
               * 눈에 들어온다. 키보드 길은 아래 목록이 맡는다(막대는 그림이다).
               */
              onClick={
                onRulePick && !onPick && segment.ruleId ? () => onRulePick(segment.ruleId as string) : undefined
              }
              data-pick={onRulePick && !onPick && segment.ruleId ? 'rule' : undefined}
            />
          );
        })}
        {[6, 12, 18].map((hour) => (
          <div key={hour} className="sp-tick" style={{ left: `${((hour * 60) / totalMinutes) * 100}%` }} />
        ))}
        {showNow && isToday ? (
          <div className="sp-now" style={{ left: `${(nowMinutes / totalMinutes) * 100}%` }} title="지금" />
        ) : null}
        {markMinutes !== null ? (
          <div
            className={`sp-mark ${scrubbing ? 'is-scrubbing' : ''}`}
            style={{ left: `${(Math.min(markMinutes, totalMinutes) / totalMinutes) * 100}%` }}
            title={`선택: ${hhmm(markMinutes)}`}
          />
        ) : null}
      </div>

      <div className="sp-axis mono">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>

      {/*
        접는 쪽에서는 목록 위에 손잡이를 둔다. 접힌 채로도 **구간이 몇 개인지는 보인다** —
        "하루가 몇 토막으로 나뉘나" 는 펼치지 않고도 알아야 할 값이다.
      */}
      {foldList ? (
        <button
          type="button"
          className={`sp-fold ${listOpen ? 'is-open' : ''}`}
          aria-expanded={listOpen}
          onClick={() => setListOpen((value) => !value)}
        >
          <ChevronDown
            className={`sp-fold-chevron ${listOpen ? '' : 'is-closed'}`}
            strokeWidth={2}
            aria-hidden="true"
          />
          <span className="sp-fold-label">구간 {segments.length}개</span>
          {/*
            **무엇을 여는 손잡이인지 글자로 말한다.**

            화살표와 "구간 12개" 만 있으면 그냥 설명 한 줄로 읽혀, 눌러서 펼칠 수 있다는
            것을 아무도 모른다 — 접어 둔 목록은 그렇게 통째로 없는 기능이 된다.
          */}
          <span className="sp-fold-hint">{listOpen ? '접기' : '시간대별로 보기'}</span>
        </button>
      ) : null}

      {listOpen ? (
        <ul className={`sp-list ${foldList ? 'is-folded' : ''}`}>
          {segments.map((segment) => {
            const tone = tones.get(segmentKey(segment) ?? '') ?? null;
            const body = (
              <>
                <span className={`sp-chip ${tone ? `sp-t${tone}` : 'sp-idle'}`} aria-hidden="true" />
                <span className="mono">
                  {hhmm(segment.fromMinutes)}~{hhmm(segment.toMinutes)}
                </span>
                <span>{nameOf(segment)}</span>
                <span className="cell-muted">{ruleLabel(segment.ruleId)}</span>
              </>
            );
            const hit = highlightRuleId !== null && segment.ruleId === highlightRuleId;
            return (
              <li
                key={segment.fromMinutes}
                className={highlightRuleId === null ? '' : hit ? 'is-hit' : 'is-dim'}
              >
                {/*
                  누르면 무엇이 일어나는지는 **주는 쪽이 정한다.** 편집기에서는 그 구간을
                  만든 규칙으로 데려가고(`onRulePick`), 화면 상세에서는 그 시각의 화면을
                  보여준다(`onPick`). 한 번 누르는 데 두 뜻을 담지 않으려고 둘을 함께
                  주지 않는다.
                */}
                {onRulePick && segment.ruleId ? (
                  <button
                    type="button"
                    className="sp-pick"
                    onClick={() => onRulePick(segment.ruleId as string)}
                    title="이 구간의 규칙으로 이동"
                  >
                    {body}
                  </button>
                ) : onPick ? (
                  <button
                    type="button"
                    className="sp-pick"
                    onClick={() => onPick(ymd, segment.fromMinutes)}
                    title={`${hhmm(segment.fromMinutes)} 의 화면 보기`}
                  >
                    {body}
                  </button>
                ) : (
                  body
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
